import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import moment, { Moment } from "moment";
import {useMutation, useQuery, useQueryClient} from "react-query";
import { enqueueSnackbar } from "notistack";
import axiosClient from "../../../../../../../lib/axios";
import { PesquisarRetencoes } from "../../../../../../../types/api/contabilidade/pesquisar-retencoes";
import {
    formatarInputValorMonetário,
    formatarMonetárioParaNúmero,
    formatarValorMonetario
} from "../../../../../../../helpers/masks";
import LoadingApiQuery from "../../../../../../../components/LoadingApiQuery";
import BasicDatePicker from "../../../../../../../components/BasicDatePicker";
import { TitPagarRetencao } from "../../../../../../../types/api/financeiro/detalhar-titulo-pagar";

interface DialogAdicionarProps {
    open: boolean;
    handleClose: () => void;
    codigo: string;
    detalhe?: TitPagarRetencao[];
    valorTotal: number;
}

export type ValoresRetencao = {
    retencao: PesquisarRetencoes | null;
    vencimento: Moment | null;
    valor: string;
    checked: boolean;
};

export default function DialogAdicionar({
                                            open,
                                            codigo,
                                            valorTotal,
                                            detalhe,
                                            handleClose
                                        }: DialogAdicionarProps) {
    const queryClient = useQueryClient();
    const [valores, setValores] = useState<ValoresRetencao[]>([]);

    const { data, isLoading } = useQuery({
        queryKey: ["contabilidade-pesquisar-retencoes"],
        queryFn: () => axiosClient.get("/contabilidade/pesquisar-retencoes"),
        onError: (error) => {
            console.log(error);
            enqueueSnackbar("Erro ao buscar retenções", {
                variant: "error"
            });
        },
        cacheTime: 0
    });

    useEffect(() => {
        if (open && !isLoading && data) {
            const listaDados: PesquisarRetencoes[] = data.data.data;

            const detalheMap = (detalhe ?? []).reduce((acc, curr) => {
                const id = String(
                    (curr.retencao as any).id ?? (curr.retencao as any).codigo
                );
                acc[id] = curr;
                return acc;
            }, {} as Record<string, TitPagarRetencao>);

            const merged = listaDados.map(retencao => {
                const preenchida = detalheMap[String(retencao.id)];
                return {
                    retencao,
                    vencimento: preenchida && preenchida.vencimento_retencao ? moment(preenchida.vencimento_retencao) : null,
                    valor: preenchida ? formatarValorMonetario(String(preenchida.valor_retencao)) : '',
                    checked: !!preenchida
                };
            });

            setValores(merged);
        }
        if (!open) setValores([]);
    }, [open, isLoading, data, detalhe]);

    const { mutate: incluirRetencao, isLoading: carregandoIncluirRetencao } = useMutation({
        mutationKey: 'incluir-retencao',
        mutationFn: () => axiosClient.post("/financeiro/contas-pagar/incluir-retencao", {
            id_titulo: codigo,
            valor_total: valorTotal,
            retencoes: valores.filter(item => item.checked).map(item => ({
                id: item.retencao?.id,
                data_vencimento: item.vencimento?.format("YYYY-MM-DD"),
                valor: formatarMonetárioParaNúmero(item.valor)
            })),
        }),
        onError: (error) => {
            console.error(error);
            enqueueSnackbar("Erro ao incluir retenção", { variant: "error" });
        },
        onSuccess: () => {
            handleClose();
            queryClient.invalidateQueries(['financeiro/contas-pagar/detalhar-titulo-pagar', codigo])
            enqueueSnackbar("Retenção incluída com sucesso", { variant: "success" });
        }
    });

    const { mutate: editarRetencao, isLoading: carregandoEditarRetencao } = useMutation({
        mutationKey: 'editar-retencao',
        mutationFn: () => axiosClient.put("/financeiro/contas-pagar/editar-retencao", {
            id_titulo: codigo,
            valor_total: valorTotal,
            retencoes: valores.filter(item => item.checked).map(item => ({
                id: item.retencao?.id,
                data_vencimento: item.vencimento?.format("YYYY-MM-DD"),
                valor: formatarMonetárioParaNúmero(item.valor)
            })),
        }),
        onError: (error) => {
            console.error(error);
            enqueueSnackbar("Erro ao editar retenção", { variant: "error" });
        },
        onSuccess: () => {
            handleClose();
            queryClient.invalidateQueries(['financeiro/contas-pagar/detalhar-titulo-pagar', codigo])
            enqueueSnackbar("Retenção editada com sucesso", { variant: "success" });
        }
    });

    const handleChangeChecked = (index: number) => {
        setValores(prev => {
            const novoValores = [...prev];
            novoValores[index] = {
                ...novoValores[index],
                vencimento: !novoValores[index].checked ? moment() : null,
                valor: !novoValores[index].checked ? 'R$ 0,00' : '',
                checked: !novoValores[index].checked
            };
            return novoValores;
        });
    };

    const handleChangeVencimento = useCallback((index: number, data: Moment | null) => {
        setValores(prev => {
            const novoValores = [...prev];
            novoValores[index] = {
                ...novoValores[index],
                vencimento: data
            };
            return novoValores;
        });
    }, []);

    const handleChangeValor = useCallback((index: number, valor: string) => {
        let valorFormatado = formatarInputValorMonetário(valor);
        const valorNumerico = formatarMonetárioParaNúmero(valorFormatado);

        setValores(prev => {
            const novoValores = [...prev];

            if (valorNumerico <= 0) {
                novoValores[index] = {
                    ...novoValores[index],
                    valor: "",
                    checked: false
                };
            } else {
                novoValores[index] = {
                    ...novoValores[index],
                    valor: valorFormatado,
                    checked: true
                };
            }
            return novoValores;
        });
    }, []);

    const handleSubmit = () => {
        if (detalhe && detalhe.length > 0) {
            editarRetencao();
        } else {
            incluirRetencao();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle>
                {detalhe && detalhe.length > 0 ? "Editar retenções" : "Adicionar retenção"}
            </DialogTitle>

            <DialogContent>
                {isLoading && <LoadingApiQuery />}

                {!isLoading && (
                    <TableContainer>
                        <Table aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell><b>Descrição</b></TableCell>
                                    <TableCell align="right"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {valores.map((row, index) => (
                                    <TableRow
                                        key={row.retencao?.id}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell component="th" scope="row">
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Checkbox
                                                    checked={row.checked}
                                                    onChange={() => handleChangeChecked(index)}
                                                    slotProps={{input: {'aria-label': 'controlled' }}}
                                                    sx={{ padding: 0 }}
                                                />
                                                <Typography variant="body2">
                                                    {`${row.retencao?.id} - ${row.retencao?.descricao}`}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Box width="50%">
                                                    <BasicDatePicker
                                                        label="Vencimento"
                                                        sizeInput="small"
                                                        blockPastDates
                                                        value={row.vencimento}
                                                        onChange={(data) => handleChangeVencimento(index, data)}
                                                        formatString="DD/MM/YYYY"
                                                        disabled={!row.checked}
                                                    />
                                                </Box>

                                                <Box width="50%">
                                                    <TextField
                                                        size="small"
                                                        label="Valor"
                                                        value={row.valor}
                                                        onChange={(e) => handleChangeValor(index, e.target.value)}
                                                        disabled={!row.checked}
                                                        error={!!row.valor && formatarMonetárioParaNúmero(row.valor) <= 0}
                                                        helperText={!!row.valor && formatarMonetárioParaNúmero(row.valor) <= 0 ? "O valor deve ser maior que zero" : ""}
                                                    />
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={
                        carregandoIncluirRetencao ||
                        carregandoEditarRetencao ||
                        valores.filter(v => v.checked).length === 0 ||
                        valores.some(v => v.checked && formatarMonetárioParaNúmero(v.valor) <= 0)
                    }
                >
                    {(carregandoIncluirRetencao || carregandoEditarRetencao) && <CircularProgress size={20} sx={{ mr: 1 }} />}
                    Confirmar
                </Button>
            </DialogActions>
        </Dialog>
    );
}